'use client'

import React, { useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'

interface AccessibilityProviderProps {
  children: React.ReactNode
}

/**
 * Applies accessibility preference classes to the <body> element
 * without disturbing layout. Preferences are stored in userProfile.preferences.
 */
export default function AccessibilityProvider({ children }: AccessibilityProviderProps) {
  const { userProfile } = useAuth()

  useEffect(() => {
    const apply = () => {
      const stored = (() => {
        try { return JSON.parse(localStorage.getItem('ui_prefs') || '{}') } catch { return {} }
      })() as Record<string, unknown>
      const prefs = { ...(userProfile?.preferences as Record<string, unknown> || {}), ...stored }

      const setClass = (enabled: boolean | undefined, className: string) => {
        if (enabled) document.body.classList.add(className)
        else document.body.classList.remove(className)
      }

      setClass(Boolean(prefs.largeText), 'pref-large-text')
      setClass(Boolean(prefs.highContrast), 'pref-high-contrast')
      setClass(Boolean(prefs.reducedMotion), 'pref-reduced-motion')
      setClass(Boolean(prefs.dyslexiaFont), 'pref-dyslexia-font')
    }

    apply()
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'ui_prefs') apply()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [userProfile])

  return <>{children}</>
}


